import {
    useCallback, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
    AdvancedMarker, ControlPosition, MapControl, Pin, useMapsLibrary,
} from '@vis.gl/react-google-maps';
import AsyncSelect from 'react-select/async';
import { getTenantTheme } from '@s20/selectors/features/common.selectors';
import './index.scss';
import ValidatedSelect from '../forms/ValidatedSelect';

const defaultFields = ['id', 'displayName', 'formattedAddress', 'location', 'viewport'];

export const GoogleSearch = ({
    fields = defaultFields,
    selectedPlace,
    onPlaceSelect,
}) => {
    const places = useMapsLibrary('places');
    const tenantTheme = useSelector(getTenantTheme);
    const [inputValue, setInputValue] = useState('');
    const [markerPosition, setMarkerPosition] = useState(null);

    // stores the current sessionToken
    const sessionTokenRef = useRef(null);

    const getSuggestions = useCallback(async (inputString) => {
        const { AutocompleteSessionToken, AutocompleteSuggestion } = places;

        // Create a new session if one doesn't already exist. This has to be reset
        // after `fetchFields` for one of the returned places is called by calling
        // the `resetSession` function returned from this hook.
        if (!sessionTokenRef.current) {
            sessionTokenRef.current = new AutocompleteSessionToken();
        }

        const request = {
            input: inputString,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes: ['us', 'ca'],
            region: 'us',
        };

        const data = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        const processedData = !data || !data.suggestions ? [] : data.suggestions.map((suggestion) => ({
            value: suggestion.placePrediction?.placeId,
            label: suggestion.placePrediction?.text.text,
            suggestion,
        }));

        return processedData;
    }, [places]);

    const handleSuggestionClick = useCallback(
        async (selectedOption) => {
            if (!selectedOption) {
                onPlaceSelect?.(null);
                sessionTokenRef.current = null;
                setInputValue('');
                setMarkerPosition(null);
                return;
            }

            const { suggestion } = selectedOption;
            if (!places) return;
            if (!suggestion.placePrediction) return;

            const place = suggestion.placePrediction.toPlace();

            await place.fetchFields({
                fields,
            });

            // calling fetchFields invalidates the session-token, so we now have to call
            // resetSession() so a new one gets created for further search
            sessionTokenRef.current = null;

            // Set the input value to the selected place's display name
            setInputValue(selectedOption.label);

            setMarkerPosition(place.location);

            if (onPlaceSelect) {
                onPlaceSelect(place);
            }
        },
        [places, onPlaceSelect, fields],
    );

    const handleInputChange = useCallback((newValue, actionMeta) => {
        // Update input value for all actions except when menu closes or input blurs
        if (actionMeta.action === 'input-change') {
            setInputValue(newValue);
        }
    }, []);

    const transformedSelectedPlace = useMemo(() => {
        if (!selectedPlace) {
            setMarkerPosition(null);
            return null;
        }

        setInputValue(selectedPlace.displayName);
        setMarkerPosition(selectedPlace.location);
        return {
            value: selectedPlace.id,
            label: 'Last Search Location: ' + selectedPlace.displayName,
        };
    }, [selectedPlace]);

    return (
        <>
            <MapControl position={ControlPosition.TOP_LEFT}>
                <ValidatedSelect
                    component={AsyncSelect}
                    value={transformedSelectedPlace}
                    onChange={handleSuggestionClick}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    loadOptions={getSuggestions}
                    className="places-search"
                    placeholder="Search for a location"
                    name="place"
                    id="place"
                    isSearchable
                    cacheOptions
                    isClearable
                />
            </MapControl>
            {markerPosition && (
                <AdvancedMarker position={markerPosition}>
                    <Pin
                        background={tenantTheme?.primaryColor ?? '#1976d2'}
                        borderColor={tenantTheme?.defaultHighlightAccentColor ?? '#115293'}
                        glyphColor={tenantTheme?.defaultHighlightAccentColor ?? '#ffffff'}
                    />
                </AdvancedMarker>
            )}
        </>
    );
};